import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  clientAuthService,
  clientNotificationService,
  partnerNotificationService,
  partnerOfferService,
  clientRevshareService,
  partnerRevshareService,
} from '../api';
import partnerAuthService from '../api/partner/partnerAuthService';
import { useNavigate } from 'react-router';
import config from '../config';
import clientCplService from '../api/client/clientCplService';
import { useTranslations } from './translation.context';

interface User {
  id: string;
  email: string;
  name?: string;
  type: 'creator' | 'marketer' | 'agency';
  role?: 'client' | 'partner';
  status?: string;
  telegram?: string;
  contact?: string;
  tg_tag?: string;
  whats_app?: string;
  balance?: number;
  avatar?: string;
  sources?: string[];
  volume?: string;
  has_only_fans_experience?: boolean;
  language?: string;
  source_info_about_us?: string;
  experience_description?: string;
  lvl?: string;
}

interface Notification {
  id: string;
  created_at: string;
  message: string;
  alert_message: string | null;
  was_read: boolean;
  owner: string;
}

interface NotificationsResponse {
  page: number;
  page_size: number;
  count: number;
  objects: Notification[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, role: 'client' | 'partner') => Promise<void>;
  signup: (data: any, role: 'client' | 'partner') => Promise<void>;
  resetPasswordClient: (data: any) => Promise<void>;
  resetPasswordPartner: (data: any) => Promise<void>;
  changePasswordClient: (data: any) => Promise<void>;
  changePasswordPartner: (data: any) => Promise<void>;
  googleAuth: () => Promise<void>;
  googleAuthPartner: () => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  loginWithToken: (token: string, role: 'client' | 'partner') => Promise<void>;
  notifications?: NotificationsResponse;
  getNotifications: (page?: number, pageSize?: number, wasRead?: boolean | null) => Promise<void>;
  markNotificationAsRead: (id: string) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  refreshUser: () => void;
  getBalance: (roleOverride?: 'client' | 'partner') => Promise<void>;
  proposalsLength: any;
  ordersLength: any;
  ordersActiveLength: any;
  revshareOffersLength: any;
  revshareCampaignsLength: any;
  revshareBillsLength: any;
  partnerOffersLength: any;
  partnerCampaignsLength: any;
  partnerActiveOffersLength: any;
  partnerOrdersLength: any;
  partnerRevshareOffersLength: any;
  partnerRevshareAccountsLength: any;
  partnerRevshareCampaignsLength: any;
  refreshPartnerCounters: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = React.memo(({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userRole, setUserRole] = useState<'client' | 'partner' | null>(null);
  const [notifications, setNotifications] = useState<NotificationsResponse | undefined>(undefined);
  const [proposalsLength, setProposalsLength] = useState<any>(0);
  const [ordersLength, setOrdersLength] = useState<any>(0);
  const [ordersActiveLength, setOrdersActiveLength] = useState<any>(0);
  const [revshareOffersLength, setRevshareOffersLength] = useState<any>(0);
  const [revshareCampaignsLength, setRevshareCampaignsLength] = useState<any>(0);
  const [revshareBillsLength, setRevshareBillsLength] = useState<any>(0);
  const [partnerOffersLength, setPartnerOffersLength] = useState<any>(0);
  const [partnerCampaignsLength, setPartnerCampaignsLength] = useState<any>(0);
  const [partnerActiveOffersLength, setPartnerActiveOffersLength] = useState<any>(0);
  const [isLoginInProgress, setIsLoginInProgress] = useState(false);
  const navigate = useNavigate();
  const getNotifications = React.useCallback(
    async (page: number = 1, pageSize: number = 10, wasRead?: boolean | null) => {
      try {
        const notificationService =
          userRole === 'partner' ? partnerNotificationService : clientNotificationService;

        const response = await notificationService.getNotifications(page, pageSize, wasRead);
        setNotifications(response.data);
      } catch (error) {
        console.error('Error getting notifications:', error);
      }
    },
    [userRole]
  );
  const getProposalsLength = React.useCallback(async () => {
    try {
      const response: any = await clientCplService.getProposals();
      setProposalsLength(response?.count || 0);
    } catch (err: any) {
      console.log('errProposals get', err);
    }
  }, []);

  const getOrdersLength = React.useCallback(async () => {
    try {
      const response: any = await clientCplService.getOrders();
      setOrdersLength(response?.count || 0);
    } catch (err: any) {
      console.log('errProposals get', err);
    }
  }, []);

  const getOrdersActiveLength = React.useCallback(async () => {
    try {
      const response: any = await clientCplService.getOrders({ status: 'in_progress' });
      setOrdersActiveLength(response?.count || 0);
    } catch (err: any) {
      console.log('errProposals get', err);
    }
  }, []);

  const getRevshareOffersLength = React.useCallback(async () => {
    try {
      const response: any = await clientRevshareService.getOffers({ page: 1, page_size: 1 });
      setRevshareOffersLength(response?.count || 0);
    } catch (err: any) {
      console.log('errRevshareOffers get', err);
    }
  }, []);

  const getRevshareCampaignsLength = React.useCallback(async () => {
    try {
      const response: any = await clientRevshareService.getOrders({ page: 1, page_size: 1 });
      setRevshareCampaignsLength(response?.count || 0);
    } catch (err: any) {
      console.log('errRevshareCampaigns get', err);
    }
  }, []);

  const getRevshareBillsLength = React.useCallback(async () => {
    try {
      const response: any = await clientRevshareService.getBills({ page: 1, page_size: 1 });
      setRevshareBillsLength(response?.count || 0);
    } catch (err: any) {
      console.log('errRevshareBills get', err);
    }
  }, []);

  // Partner-specific data fetching functions
  const getPartnerNotifications = React.useCallback(
    async (page: number = 1, pageSize: number = 10) => {
      try {
        const response = await partnerNotificationService.getNotifications(page, pageSize);
        setNotifications(response.data);
      } catch (error) {
        console.error('Error getting partner notifications:', error);
      }
    },
    []
  );

  const getPartnerOffersLength = React.useCallback(async () => {
    try {
      const response: any = await partnerOfferService.getOffers();
      setPartnerOffersLength(response?.count || 0);
    } catch (err: any) {
      console.log('errPartnerOffers get', err);
    }
  }, []);

  // const getPartnerCampaignsLength = React.useCallback(async () => {
  //   try{
  //     //@ts-ignore
  //     const response: any = await partnerOfferService.getCampaigns();
  //     setPartnerCampaignsLength(response?.count || 0)
  //   } catch (err:any) {
  //     console.log('errPartnerCampaigns get', err);
  //   }
  // }, []);

  const getPartnerActiveOffersLength = React.useCallback(async () => {
    try {
      const response: any = await partnerOfferService.getOffers({ status: 'active' });
      setPartnerActiveOffersLength(response?.count || 0);
    } catch (err: any) {
      console.log('errPartnerActiveOffers get', err);
    }
  }, []);

  const [partnerOrdersLength, setPartnerOrdersLength] = useState<any>(0);
  const [partnerRevshareOffersLength, setPartnerRevshareOffersLength] = useState<any>(0);
  const [partnerRevshareAccountsLength, setPartnerRevshareAccountsLength] = useState<any>(0);
  const [partnerRevshareCampaignsLength, setPartnerRevshareCampaignsLength] = useState<any>(0);

  const getPartnerOrdersLength = React.useCallback(async () => {
    try {
      const response: any = await partnerOfferService.getOrders();
      setPartnerOrdersLength(response?.count || 0);
    } catch (err: any) {
      console.log('errPartnerOrders get', err);
    }
  }, []);

  const getPartnerRevshareOffersLength = React.useCallback(async () => {
    try {
      const response: any = await partnerRevshareService.getOffers({ page: 1, page_size: 1 });
      console.log('partnerRevshareOffers response:', response);
      const count = response?.count ?? 0;
      console.log('partnerRevshareOffers count:', count);
      setPartnerRevshareOffersLength(count);
    } catch (err: any) {
      console.error('errPartnerRevshareOffers get:', err);
      setPartnerRevshareOffersLength(0);
    }
  }, []);

  const getPartnerRevshareAccountsLength = React.useCallback(async () => {
    try {
      const response: any = await partnerRevshareService.getCompaniesModels({
        page: 1,
        page_size: 1,
      });
      setPartnerRevshareAccountsLength(response?.count || 0);
    } catch (err: any) {
      console.log('errPartnerRevshareAccounts get', err);
    }
  }, []);

  const getPartnerRevshareCampaignsLength = React.useCallback(async () => {
    try {
      const response: any = await partnerRevshareService.getOrders({ page: 1, page_size: 1 });
      setPartnerRevshareCampaignsLength(response?.count || 0);
    } catch (err: any) {
      console.log('errPartnerRevshareCampaigns get', err);
    }
  }, []);

  const refreshPartnerCounters = React.useCallback(async () => {
    try {
      await Promise.all([
        getPartnerOffersLength(),
        getPartnerActiveOffersLength(),
        getPartnerOrdersLength(),
        getPartnerRevshareOffersLength(),
        getPartnerRevshareAccountsLength(),
        getPartnerRevshareCampaignsLength(),
      ]);
    } catch (err) {
      console.log('Error refreshing partner counters:', err);
    }
  }, [
    getPartnerOffersLength,
    getPartnerActiveOffersLength,
    getPartnerOrdersLength,
    getPartnerRevshareOffersLength,
    getPartnerRevshareAccountsLength,
    getPartnerRevshareCampaignsLength,
  ]);

  // Helper function to map backend user types to frontend roles
  const mapUserTypeToRole = (userType: string): 'client' | 'partner' => {
    // Backend types that map to partner role
    if (userType === 'marketer' || userType === 'partner') {
      return 'partner';
    }
    return 'client'; // 'creator', 'agency', or any other type defaults to 'client'
  };

  // Helper function to check if user should have client data
  const isClientUser = (userType: string): boolean => {
    return userType === 'creator' || userType === 'agency';
  };

  // Helper function to check if user should have partner data
  const isPartnerUser = (userType: string): boolean => {
    return userType === 'marketer';
  };
  const getBalance = React.useCallback(
    async (roleOverride?: 'client' | 'partner') => {
      try {
        const token = localStorage.getItem('authToken');
        const roleFromUser = user?.type
          ? mapUserTypeToRole(user.type)
          : (user?.role as 'client' | 'partner' | undefined);
        const resolvedRole =
          roleOverride ||
          roleFromUser ||
          userRole ||
          (localStorage.getItem('userRole') as 'client' | 'partner' | null);

        if (token && resolvedRole) {
          const { setClientToken } = await import('../api/apiClient');
          setClientToken(token);

          const response =
            resolvedRole === 'partner'
              ? await partnerAuthService.getBalance()
              : await clientAuthService.getBalance();

          const balanceRaw = response?.data?.amount ?? response?.data?.balance ?? 0;
          const balance = Number(balanceRaw) || 0;

          setUser((prevUser) => {
            if (prevUser) {
              return { ...prevUser, balance: balance };
            }
            return prevUser;
          });
        }
      } catch (error) {
        console.error('Failed to get balance:', error);
      }
    },
    [userRole, user?.role]
  );

  const getPartnerBalance = () => {
    getBalance('partner');
  };
  const markNotificationAsRead = React.useCallback(
    async (id: string) => {
      try {
        // Use the same notification service for both roles since endpoints are unified
        const notificationService =
          userRole === 'partner' ? partnerNotificationService : clientNotificationService;

        await notificationService.setWasRead(id);
        // Обновляем локальное состояние
        if (notifications) {
          const updatedObjects = notifications.objects.map((n) =>
            n.id === id ? { ...n, was_read: true } : n
          );
          setNotifications({
            ...notifications,
            objects: updatedObjects,
          });
        }
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },
    [notifications, userRole]
  );

  const markAllNotificationsAsRead = React.useCallback(async () => {
    try {
      // Use the same notification service for both roles since endpoints are unified
      const notificationService =
        userRole === 'partner' ? partnerNotificationService : clientNotificationService;

      await notificationService.setWasReadAll();
      // Обновляем локальное состояние
      if (notifications) {
        const updatedObjects = notifications.objects.map((n) => ({ ...n, was_read: true }));
        setNotifications({
          ...notifications,
          objects: updatedObjects,
        });
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [notifications, userRole]);

  const checkAuth = React.useCallback(async () => {
    // Prevent checkAuth from running during login process
    if (isLoginInProgress) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const storedRole = localStorage.getItem('userRole') as 'client' | 'partner' | null;

      if (token) {
        // Set authorization header first
        const { setClientToken } = await import('../api/apiClient');
        setClientToken(token);

        // If we have stored role, use it directly
        if (storedRole) {
          try {
            const authService =
              storedRole === 'client'
                ? (await import('../api/client/clientAuthService')).default
                : (await import('../api/partner/partnerAuthService')).default;

            const response = await authService.getMe({});
            const userData = { ...response.data, role: storedRole };

            setUser(userData);
            setUserRole(storedRole);

            // Fetch data based on actual user type
            const actualUserType = userData.type;
            if (isClientUser(actualUserType)) {
              await getNotifications();
              await getBalance();
              await getOrdersLength();
              await getProposalsLength();
              await getOrdersActiveLength();
              await getRevshareOffersLength();
              await getRevshareCampaignsLength();
              await getRevshareBillsLength();
            } else {
              await getPartnerNotifications();
              await getPartnerBalance();
              await getPartnerOrdersLength();
              await getPartnerOffersLength();
              await getPartnerActiveOffersLength();
              await getPartnerRevshareOffersLength();
              await getPartnerRevshareAccountsLength();
              await getPartnerRevshareCampaignsLength();
            }
          } catch (error) {
            console.error(`Failed to fetch ${storedRole} data:`, error);
            // Don't logout immediately, token might still be valid
            // Just clear the stored role and try to determine it again
            localStorage.removeItem('userRole');
            setUserRole(null);
          }
        } else {
          // Try to determine user role
          try {
            const clientAuthService = (await import('../api/client/clientAuthService')).default;
            const response = await clientAuthService.getMe({});
            const userData = { ...response.data, role: 'client' as const };
            setUser(userData);
            setUserRole('client');

            // Fetch data based on actual user type
            const actualUserType = userData.type;
            if (isClientUser(actualUserType)) {
              await getNotifications();
              await getBalance();
              await getOrdersLength();
              await getProposalsLength();
              await getOrdersActiveLength();
              await getRevshareOffersLength();
              await getRevshareCampaignsLength();
              await getRevshareBillsLength();
            }
            localStorage.setItem('userRole', 'client');
          } catch (clientError) {
            try {
              const partnerAuthService = (await import('../api/partner/partnerAuthService'))
                .default;
              const response = await partnerAuthService.getMe({});
              const userData = { ...response.data, role: 'partner' as const };
              setUser(userData);
              setUserRole('partner');

              // Fetch data based on actual user type
              const actualUserType = userData.type || 'marketer';
              {
                await getPartnerNotifications();
                await getPartnerBalance();
                await getPartnerOrdersLength();
                await getPartnerOffersLength();
                await getPartnerActiveOffersLength();
                await getPartnerRevshareOffersLength();
                await getPartnerRevshareAccountsLength();
                await getPartnerRevshareCampaignsLength();
              }
              localStorage.setItem('userRole', 'partner');
            } catch (partnerError) {
              console.error('Failed to fetch user data from both services');
              logout();
            }
          }
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, [
    isInitialized,
    isLoginInProgress,
    getNotifications,
    getBalance,
    getOrdersLength,
    getProposalsLength,
    getOrdersActiveLength,
    getPartnerNotifications,
    getPartnerBalance,
    getPartnerOrdersLength,
    getPartnerOffersLength,
    getPartnerActiveOffersLength,
    getPartnerRevshareOffersLength,
    getPartnerRevshareAccountsLength,
    getPartnerRevshareCampaignsLength,
  ]); // Мемоизируем функцию

  const login = React.useCallback(
    async (email: string, password: string, role: 'client' | 'partner') => {
      try {
        // setIsLoading(true);
        setIsLoginInProgress(true);

        // Determine which service to use based on role
        const authService =
          role === 'client'
            ? (await import('../api/client/clientAuthService')).default
            : (await import('../api/partner/partnerAuthService')).default;

        const response = await authService.signIn({ email, password });

        const { access_token } = response.data;

        const token = `Bearer ${access_token}`;

        // Store token and role
        localStorage.setItem('authToken', token);
        localStorage.setItem('userRole', role);

        // Set authorization header
        const { setClientToken } = await import('../api/apiClient');
        setClientToken(token);

        // Fetch complete user data with getMe call
        const userResponse = await authService.getMe({});
        const completeUserData = { ...userResponse.data, role: role };

        setUser(completeUserData);
        setUserRole(role);

        // Получаем все данные пользователя после успешного логина
        // Check the actual user type from response to determine what data to fetch
        const actualUserType = completeUserData.type; // Default to creator if not specified

        if (isClientUser(actualUserType)) {
          // Fetch client-specific data
          await getNotifications();
          await getBalance();
          await getOrdersLength();
          await getProposalsLength();
          await getOrdersActiveLength();
          await getRevshareOffersLength();
          await getRevshareCampaignsLength();
          await getRevshareBillsLength();
        } else {
          // Fetch partner-specific data
          await getPartnerNotifications();
          await getPartnerBalance();
          await getPartnerOrdersLength();
          await getPartnerOffersLength();
          await getPartnerActiveOffersLength();
          await getPartnerRevshareOffersLength();
          await getPartnerRevshareAccountsLength();
          await getPartnerRevshareCampaignsLength();
        }

        setIsInitialized(true);
        navigate(role === 'partner' ? '/partner/dashboard' : '/client/dashboard', {
          replace: true,
        });
      } catch (error) {
        console.error('Login failed:', error);
        throw error;
      } finally {
        setIsLoading(false);
        setIsLoginInProgress(false);
      }
    },
    [
      getNotifications,
      getBalance,
      getOrdersLength,
      getProposalsLength,
      getOrdersActiveLength,
      getPartnerNotifications,
      getPartnerBalance,
      getPartnerOrdersLength,
      getPartnerOffersLength,
      getPartnerActiveOffersLength,
      getPartnerRevshareOffersLength,
      getPartnerRevshareAccountsLength,
      getPartnerRevshareCampaignsLength,
      navigate,
    ]
  );
  const signup = async (data: any, role: 'client' | 'partner') => {
    try {
      // setIsLoading(true);

      // Determine which service to use based on role
      const authService =
        role === 'client'
          ? (await import('../api/client/clientAuthService')).default
          : (await import('../api/partner/partnerAuthService')).default;

      const response = await authService.signUp(data);

      const { access_token, user: userData } = response.data;

      const token = `Bearer ${access_token}`;

      // Create user object with role if not provided by backend
      const user = userData || {
        id: 'temp-id',
        email: data.email,
        role: role,
      };

      // Don't store anything for signup, redirect to login
      // await navigate(`/${role === 'client' ? 'client' : 'partner' }/sign-in`);
      // Set authorization header
      // const { setClientToken } = await import('../api/apiClient');
      // setClientToken(token);

      // setUser(user);
    } catch (error) {
      console.error('Signup failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPasswordClient = async (data: any) => {
    try {
      // setIsLoading(true);

      const response = await clientAuthService.resetPassword(data);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPasswordPartner = async (data: any) => {
    try {
      // setIsLoading(true);

      // TODO: Implement partner reset password service
      // For now, use client service as placeholder
      const response = await partnerAuthService.resetPassword(data);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  const changePasswordClient = async (data: any) => {
    try {
      // setIsLoading(true);
      let preData = {
        password: data.password,
      };
      const response = await clientAuthService.changePassword(preData, data.token);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const changePasswordPartner = async (data: any) => {
    try {
      // setIsLoading(true);
      let preData = {
        password: data.password,
      };
      // TODO: Implement partner reset password service
      // For now, use client service as placeholder
      const response = await partnerAuthService.changePassword(preData, data.token);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  const googleAuth = async () => {
    try {
      // const response = await clientAuthService.googleAuth();

      // console.log(response);
      window.location.href = `${config.apiUrl}auth/google/client/login`;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const googleAuthPartner = async () => {
    try {
      // Временно используем клиентский endpoint с параметром для партнера
      window.location.href = `${config.apiUrl}auth/google/partner/login`;
    } catch (error) {
      console.error('Partner Google auth failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const storedRole = localStorage.getItem('userRole') as 'client' | 'partner' | null;

      if (token && storedRole) {
        // Ensure token is set before making the request
        const { setClientToken } = await import('../api/apiClient');
        setClientToken(token);

        const authService =
          storedRole === 'client'
            ? (await import('../api/client/clientAuthService')).default
            : (await import('../api/partner/partnerAuthService')).default;

        const response = await authService.getMe({});
        const userData = { ...response.data, role: storedRole };
        setUser(userData);

        // Also refresh user-specific data based on type
        const actualUserType = userData.type;
        if (isClientUser(actualUserType)) {
          // Optionally refresh client data - uncomment if needed
          // await getNotifications();
          // await getBalance();
        } else {
          // Optionally refresh partner data - uncomment if needed
          // await getPartnerNotifications();
          // await getPartnerBalance();
        }
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      // Don't logout on refresh failure
    }
  };

  const logout = React.useCallback(() => {
    const storedRole =
      userRole || (localStorage.getItem('userRole') as 'client' | 'partner' | null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');

    const { resetClientToken } = require('../api/apiClient');
    resetClientToken();

    setUser(null);
    setUserRole(null);
    setIsInitialized(false);

    // Set loading to false immediately to allow route guards to work
    setTimeout(() => {
      setIsLoading(false);
    }, 0);

    if (storedRole) {
      navigate(storedRole === 'partner' ? '/partner/sign-in' : '/client/sign-in', {
        replace: true,
      });
    } else {
      navigate('/client/sign-in', { replace: true });
    }
  }, [navigate, userRole]);

  // Авторизация по токену (без email/password)
  const loginWithToken = React.useCallback(
    async (token: string, role: 'client' | 'partner') => {
      try {
        setIsLoading(true);
        const bearerToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        localStorage.setItem('authToken', bearerToken);
        localStorage.setItem('userRole', role);
        const { setClientToken } = await import('../api/apiClient');
        setClientToken(bearerToken);
        const authService =
          role === 'client'
            ? (await import('../api/client/clientAuthService')).default
            : (await import('../api/partner/partnerAuthService')).default;
        const response = await authService.getMe({});
        const userData = { ...response.data, role };
        setUser(userData);

        const actualUserType = userData.type; // Default to creator if not specified

        if (isClientUser(actualUserType)) {
          // Fetch client-specific data
          await getNotifications();
          await getBalance();
          await getOrdersLength();
          await getProposalsLength();
          await getOrdersActiveLength();
          await getRevshareOffersLength();
          await getRevshareCampaignsLength();
          await getRevshareBillsLength();
        } else {
          // Fetch partner-specific data
          await getPartnerNotifications();
          await getPartnerBalance();
          await getPartnerOrdersLength();
          await getPartnerOffersLength();
          await getPartnerActiveOffersLength();
          await getPartnerRevshareOffersLength();
          await getPartnerRevshareAccountsLength();
          await getPartnerRevshareCampaignsLength();
        }
        navigate(role === 'partner' ? '/partner/dashboard' : '/client/dashboard', {
          replace: true,
        });
      } catch (error) {
        console.error('Login with token failed:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [
      getNotifications,
      getBalance,
      getOrdersLength,
      getProposalsLength,
      getOrdersActiveLength,
      getRevshareBillsLength,
      getPartnerNotifications,
      getPartnerBalance,
      getPartnerOrdersLength,
      getPartnerOffersLength,
      getPartnerActiveOffersLength,
      getPartnerRevshareOffersLength,
      getPartnerRevshareAccountsLength,
      getPartnerRevshareCampaignsLength,
      navigate,
    ]
  );

  useEffect(() => {
    checkAuth();
  }, []); // Вызывается только один раз при монтировании

  const isAuthenticated = React.useMemo(() => !!user, [user]);

  // Автообновление уведомлений каждые 10 секунд
  useEffect(() => {
    if (isAuthenticated && user && userRole) {
      const interval = setInterval(() => {
        // Используем userRole для определения какие данные обновлять
        if (userRole === 'client' && isClientUser(user.type)) {
          // Обновляем только клиентские данные
          getNotifications();
          getBalance();
          getProposalsLength();
          getOrdersLength();
          getOrdersActiveLength();
          getRevshareOffersLength();
          getRevshareCampaignsLength();
          getRevshareBillsLength();
        } else if (userRole === 'partner' && isPartnerUser(user.type)) {
          // Обновляем только партнерские данные
          getPartnerNotifications();
          getPartnerBalance();
          getPartnerOrdersLength();
          getPartnerOffersLength();
          getPartnerActiveOffersLength();
          getPartnerRevshareOffersLength();
          getPartnerRevshareAccountsLength();
          getPartnerRevshareCampaignsLength();
        }
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [
    isAuthenticated,
    user,
    userRole,
    getNotifications,
    getBalance,
    getProposalsLength,
    getOrdersLength,
    getOrdersActiveLength,
    getRevshareOffersLength,
    getRevshareCampaignsLength,
    getRevshareBillsLength,
    getPartnerNotifications,
    getPartnerBalance,
    getPartnerOrdersLength,
    getPartnerOffersLength,
    getPartnerActiveOffersLength,
    getPartnerRevshareOffersLength,
    getPartnerRevshareAccountsLength,
    getPartnerRevshareCampaignsLength,
  ]);
  const value: AuthContextType = React.useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      signup,
      resetPasswordClient,
      resetPasswordPartner,
      changePasswordClient,
      changePasswordPartner,
      logout,
      checkAuth,
      refreshUser,
      googleAuth,
      googleAuthPartner,
      loginWithToken,
      notifications,
      getNotifications,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      getBalance,
      proposalsLength,
      ordersLength,
      ordersActiveLength,
      revshareOffersLength,
      revshareCampaignsLength,
      revshareBillsLength,
      partnerOffersLength,
      partnerCampaignsLength,
      partnerActiveOffersLength,
      partnerOrdersLength,
      partnerRevshareOffersLength,
      partnerRevshareAccountsLength,
      partnerRevshareCampaignsLength,
      refreshPartnerCounters,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
      checkAuth,
      refreshUser,
      loginWithToken,
      notifications,
      getNotifications,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      getBalance,
      proposalsLength,
      ordersLength,
      ordersActiveLength,
      revshareOffersLength,
      revshareCampaignsLength,
      revshareBillsLength,
      partnerOffersLength,
      partnerCampaignsLength,
      partnerActiveOffersLength,
      partnerOrdersLength,
      partnerRevshareOffersLength,
      partnerRevshareAccountsLength,
      partnerRevshareCampaignsLength,
      refreshPartnerCounters,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
});

AuthProvider.displayName = 'AuthProvider';
